<template>
  <main>
    <section
      v-if="role == 'TEACHER' && data.adaptive"
      id="blacklist-info"
      ref="blacklist-info"
      class="p-7"
    >
      <div
        class="bg-h-primary-50 p-5 border-2 border-h-primary-100 rounded-md flex justify-between items-start"
      >
        <div class="flex flex-col">
          <div class="flex gap-3 items-center">
            <img :src="require('../../../assets/images/blacklist.svg')" />
            <h4>
              {{
                $t("labels.generic", {
                  pt: "Analisar palavras chaves",
                  en: "Analyze keywords",
                })
              }}
            </h4>
          </div>
          <h6 class="ml-5">
            {{
              $t("labels.generic", {
                pt: "Os alunos poderão clicar nas palavras-chaves geradas dos vídeos e ver conteúdos relacionados, faça a análise desses itens, clique dentro dos objetivos, na opção “Analisar palavras-chaves”.",
                en: "Students will be able to click on the keywords generated from the videos and see related content, analyze these items, click within the objectives, on the “Analyze keywords” option.",
              })
            }}
          </h6>
        </div>
        <Button
          icon="pi pi-times"
          class="btn-simple p-button-sm"
          @click="closeInfo"
        />
      </div>
    </section>
    <Topbar
      :data="data"
      :role="user.role"
      @openAdaptiveChapter="openModalChapter"
    />

    <Loader :loader="loader" />

    <Adaptive
      ref="adaptive"
      v-if="data.adaptive && !loader"
      :graph="graph"
      :role="user.role"
      @onAddChapter="openModalChapter"
      @onAddObjective="addObjective"
      @onEditObjective="editObjective"
      @onShowObjects="getObjectsByLearningObjective"
      @onShowBlacklist="goToBlacklist"
    />

    <Traditional
      v-if="!data.adaptive && !loader"
      :role="user.role"
      :data="data"
    />

    <MaterialModal
      v-if="configModal.showMaterials"
      :materials="materials"
      :learningObjective="learningObjective"
      :config="configModal"
      @onChooseMaterial="getObjectsByLearningObjective"
      @onCloseMaterialsModal="closeMaterialsModal"
      @onGoToObjective="goToObjective"
      :userRole="user.role"
    />

    <Dialog
      v-model:visible="displayModal"
      :header="
        chapterModal?.name == '' ? $t('chapters.add') : $t('chapters.edit')
      "
      :draggable="false"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '40vw' }"
      :modal="true"
      class="custom-modal"
      @hide="closeModalChapter()"
    >
      <form
        @submit.prevent="submitChapter()"
        class="grid grid-cols-12 gap-5 pt-8 font-[600]"
        autocomplete="off"
      >
        <div class="col-span-12">
          <p class="mb-2">
            {{ $t("chapters.form.name") }}
          </p>
          <InputText
            :disabled="user.role === 'ADMINISTRATOR'"
            id="title-input"
            type="text"
            name="name"
            v-model="formChapter.name"
            :maxlength="50"
            :class="v$.formChapter.name.$error ? 'p-invalid' : ''"
          />
          <Errors
            :errors="v$.formChapter.name.$errors"
            :actualLength="formChapter.name?.length"
            :maxLength="50"
          />
        </div>
        <div
          class="col-span-12"
          v-if="formChapter?.id === '' && chapters?.length !== 0"
        >
          <p :class="`mb-2`">
            {{ $t("chapters.form.predecessor") }}
          </p>
          <AutoComplete
            id="predecessor-input"
            v-model="autocomplete.selected"
            :suggestions="autocomplete.suggestions"
            :forceSelection="true"
            @complete="searchForChapters($event)"
            optionLabel="name"
            :class="`w-full`"
          />
        </div>
        <div class="col-span-12">
          <p class="mb-2">
            {{ $t("chapters.form.description") }}
          </p>
          <Textarea
            id="description-input"
            :autoResize="true"
            rows="5"
            cols="30"
            v-model="formChapter.description"
            :class="v$.formChapter.description.$error ? 'p-invalid' : ''"
            :maxlength="999"
            :disabled="user.role === 'ADMINISTRATOR'"
          />
          <Errors
            :errors="v$.formChapter.description.$errors"
            :actualLength="formChapter.description?.length"
            :maxLength="999"
          />
        </div>
      </form>
      <template #footer>
        <Button
          id="cancel-input"
          :label="$t('labels.cancel')"
          class="btn-simple p-button-sm"
          @click="closeModalChapter()"
          v-if="user.role === 'TEACHER'"
        />
        <Button
          id="save-input"
          :label="$t('labels.save')"
          class="btn-primary p-button-sm"
          type="submit"
          @click="submitChapter()"
          v-if="user.role === 'TEACHER'"
        />
        <Button
          id="adm-btn"
          label="OK"
          class="btn-primary p-button-sm"
          @click="closeModalChapter()"
          v-if="user.role === 'ADMINISTRATOR'"
        />
      </template>
    </Dialog>
  </main>
</template>

<script lang="ts">
import { LearningMapService } from "@/components/LearningMap/learning-map.service";
import { required } from "@/locale/locale.config";
import { Chapter } from "@/models/chapter.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { ConfigModal, Material } from "@/models/material.model";
import { QueryParams } from "@/models/query-params.model";
import { ChaptersRest } from "@/services/rest/chapters.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { setItemLocalStorage } from "@/utils/save-storage.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import useVuelidate from "@vuelidate/core";
import { last, take } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { ChaptersService } from "../chapters.service";

const Topbar = defineAsyncComponent(() => import("./topbar.vue"));
const Adaptive = defineAsyncComponent(() => import("./adaptive.vue"));
const Traditional = defineAsyncComponent(() => import("./traditional.vue"));

export default defineComponent({
  name: "contentChapters",
  components: { Topbar, Adaptive, Traditional },
  computed: {
    rest(): ChaptersRest {
      return new ChaptersRest();
    },
    service(): ChaptersService {
      return new ChaptersService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    serviceLearningMap(): LearningMapService {
      return new LearningMapService();
    },
    user(): any {
      return getUserFromStorage();
    },
  },
  props: {
    data: { type: Object, default: {} },
    role: [String],
  },
  data() {
    return {
      formChapter: new Chapter(),
      chapterModal: new Chapter(),
      chapters: [] as Chapter[],
      materials: [] as Material[],
      today: new Date() as Date,
      autocomplete: {
        suggestions: [] as any[],
        selected: null as any,
      },
      emptyState: {
        chapters: {
          image: "empty-chapters.svg",
          description: this.$t("course.empty.chapters"),
        },
      } as any,
      displayModal: false as boolean,
      courseId: this.$route.params.id as string,
      learningObjective: {} as LearningObjective,
      maxIndexMaterial: 1 as number,
      params: new QueryParams(),
      userId: "" as string,
      graph: [] as any,
      loader: true as boolean,
      configModal: new ConfigModal(),
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      formChapter: {
        name: { required },
        description: { required },
      },
    };
  },
  mounted() {
    setTimeout(() => {
      if (this.data.adaptive) {
        this.getChaptersByCourse();
        this.getNodesByCourse();
      }
      this.userId = getUserFromStorage()?.id;
      this.loader = false;
    }, 800);
  },
  methods: {
    preventToggle(event: any): boolean {
      event.stopPropagation();
      event.preventDefault();
      return false;
    },
    goToObjective(
      courseId: string,
      chapterId: string,
      objectiveId?: string
    ): void {
      const params: any = {
        courseId: courseId,
        chapterId: chapterId,
        id: objectiveId,
        tab: 0,
      };

      if (!objectiveId) {
        const filterByChapter = this.chapters.filter((e) => e.id === chapterId);
        setItemLocalStorage("length", JSON.stringify(filterByChapter[0]));
      }

      this.go.redirectToPage("formObjective", params);
    },
    goToBlacklist(event: any): void {
      const { name } = this.chapters.filter((e) => e.id === event.chapter)[0];
      const data = { chapter: name, objective: event.name };
      setItemLocalStorage("blacklistData", JSON.stringify(data));
      this.go.redirectToPage("blacklist", { objectiveId: event.id });
    },
    openModalChapter(chapter?: Chapter): void {
      this.displayModal = true;
      this.formChapter = chapter ?? new Chapter();
      this.chapterModal.name = this.formChapter.name;
    },
    closeModalChapter(): void {
      this.v$.formChapter.$reset();
      this.formChapter = new Chapter();
      this.autocomplete.selected = null;
      this.displayModal = false;
    },
    submitChapter(): void {
      this.v$.formChapter.$touch();
      if (!this.v$.formChapter.$error) {
        const data = new Chapter();
        data.id = this.formChapter.id ?? undefined;
        data.name = this.formChapter.name;
        data.course = this.formChapter.course;
        data.description = this.formChapter.description;
        data.predecessorId = this.autocomplete.selected?.id ?? undefined;
        if (this.formChapter.id) this.updateChapter(data);
        else this.createChapter(data);
      }
    },
    createChapter(data: Chapter): void {
      this.rest.postChapter(this.courseId, data).subscribe({
        next: (response) => {
          this.chapters.push(response);
          data.id = response.id;
          if (this.graph.nodes?.length > 0) this.addNodeChapter(data);
          else this.getNodesByCourse();
          this.displayModal = false;
          this.$toast.add(ToastFactory.success(Messages.CHAPTER_ADDED));
        },
      });
    },
    updateChapter(data: Chapter): void {
      this.rest.updateChapter(this.courseId, data.id, data).subscribe({
        next: () => {
          this.addNodeChapter(data);
          this.displayModal = false;
          this.$toast.add(ToastFactory.success(Messages.CHAPTER_UPDATED));
        },
      });
    },
    searchForChapters(event: any): void {
      const { query } = event;
      const params = {
        name: query.toLowerCase(),
      };
      this.service.chapters.subscribe({
        next: (response) => {
          this.autocomplete.suggestions = response.filter(
            (e: Chapter) => e.id !== this.formChapter.id
          );
        },
      });
      this.service.getChaptersByCourse(this.courseId, params);
    },
    getChaptersByCourse(): void {
      this.service.chapters.subscribe({
        next: (response) => {
          this.chapters = response;
        },
      });
      this.service.getChaptersByCourse(this.courseId);
    },
    addNodeChapter(data: Chapter): void {
      const result = this.$refs["adaptive"] as any;
      result.$refs["learningMap"].addNodeChapter(data);
    },
    addObjective(node: any): void {
      if (node?.chapter) {
        setItemLocalStorage("objectiveId", JSON.stringify(node.id));
        this.goToObjective(this.courseId, node.chapter);
      } else this.goToObjective(this.courseId, node.id);
    },
    editObjective(node: any): void {
      this.goToObjective(this.courseId, node?.chapter, node?.id);
    },
    getObjectsByLearningObjective(
      learningObjective: LearningObjective,
      page = 0 as number
    ): void {
      const params = new QueryParams(page, 1);
      this.service.material.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.materials = response?.content;
          if (this.materials) {
            this.configModal.showMaterials = true;
            this.learningObjective = learningObjective;
            this.configModal.maxIndexMaterial = response.total - 1;
            this.configModal.typeModalHeader = this.user.role;
            this.configModal.currentIndex = page;
          }
        },
      });

      this.service.getObjectsByLearningObjective(learningObjective.id, params);
    },
    closeMaterialsModal(): void {
      this.configModal.showMaterials = false;
    },
    getNodesByCourse(): void {
      this.serviceLearningMap.graphs.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.graph = response;
        },
      });
      this.serviceLearningMap.getGraphsByCourse(this.courseId);
    },
    closeInfo(): void {
      (this.$refs["blacklist-info"] as any).hidden = true;
    },
  },
});
</script>
